import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import debounce from 'lodash.debounce'
import { Stack, Button } from '@mui/material'
import { useStytchIsAuthorized, useStytchMember } from '@stytch/react/b2b'
import { Member } from '@stytch/vanilla-js'
import { QueryKey, useQueryClient } from '@tanstack/react-query'
import {
  useGetAuthRoles,
  useUpdateAuthUser,
} from '@/service-library/hooks/auth-user'
import { useInviteMember } from '@/services/hooks/stytch'
import useIsSuperUserVisible from '@/services/hooks/useIsSuperUserVisible'
import { getHighestRole, DEFAULT_USER_ROLE } from '@/utils/auth-user'
import { showErrorSnackbar, showInfoSnackbar } from '@/utils/snackbars'
import CopyIDButton from '@/components/copy-id-button/CopyIDButton'
import { FormTextField, PixyDocsForm } from '@/components/forms'
import LabeledData from '@/components/labeled-data/LabeledData'
import LargeHeading from '@/components/large-heading/LargeHeading'
import UserRolesPicker from './UserRolesPicker'

type FormValues = {
  employee_ext_id: string
  role_id: string
}

type UserDetailsProps = {
  member: Member
  queryKey: QueryKey
}

const primeAdminsMap = {
  'tyson@pixydocs.com': 'Lord Emperor of the Eternal Mandate',
  'carl@pixydocs.com': 'Sovereign Printus Maximus',
  'toby@pixydocs.com': 'Highprince of Laterculus Modularis Lēgōnis',
  'zach@pixydocs.com': 'Supreme Strategos of the Pixy Imperium',
  'joyce@pixydocs.com': 'Sentinel Grand Vizier',
  'chad@pixydocs.com': 'Praetor Chaddus Radicus, First of His Name',
  'josh@pixydocs.com': 'Supreme Artificer',
  'artem@pixydocs.com': 'Duke of Quiet Aptitude',
  'sam@pixydocs.com': 'da guy who does da sales',
}

export default function UserDetails({ member, queryKey }: UserDetailsProps) {
  const { member: loggedInMember } = useStytchMember()
  const isSuperUserVisible = useIsSuperUserVisible()
  const queryClient = useQueryClient()

  const { inviteMember, isPending: isInvitePending } = useInviteMember({
    onSuccess: () => {
      showInfoSnackbar('Invitation email has been resent.')
    },
    onError: () => {
      showErrorSnackbar(
        'Unable to resend invitation email. Please try again later.',
      )
    },
  })

  const { isAuthorized: canEditRoles } = useStytchIsAuthorized(
    'pd.user',
    'edit.roles',
  )

  const { roles } = useGetAuthRoles()
  const rolesMap = useMemo(
    () => new Map(roles.map((role) => [role.id, role])),
    [roles],
  )

  const memberRole = useMemo(
    () => getHighestRole(member.roles, rolesMap),
    [member.roles, rolesMap],
  )

  const canUpdateRole = useMemo(() => {
    if (!canEditRoles || memberRole.id === 'Prime Admin') return false
    const loggedInMemberRole = getHighestRole(
      loggedInMember?.roles || [],
      rolesMap,
    )
    return loggedInMemberRole.order >= memberRole.order
  }, [
    canEditRoles,
    loggedInMember?.roles,
    memberRole.id,
    memberRole.order,
    rolesMap,
  ])

  const defaultValues = useMemo(
    () => ({
      employee_ext_id:
        typeof member.trusted_metadata?.employee_ext_id === 'string'
          ? member.trusted_metadata.employee_ext_id
          : '',
      role_id: memberRole.id,
    }),
    [member.trusted_metadata, memberRole.id],
  )

  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'onChange',
  })

  const { subscribe, reset } = methods

  const { updateAuthUser } = useUpdateAuthUser({
    onError: () => {
      reset()
      showErrorSnackbar('Unable to update user roles. Please try again later.')
    },
    onSettled: () => {
      // Invalidate the members list to get updated data
      queryKey && queryClient.invalidateQueries({ queryKey })
    },
  })

  const debouncedUpdateUser = useMemo(
    () => debounce(updateAuthUser, 1000),
    [updateAuthUser],
  )

  const isLoggedInMember = loggedInMember?.member_id === member.member_id

  useEffect(() => {
    const callback = subscribe({
      formState: {
        values: true,
        isDirty: true,
      },
      callback: ({ values, isDirty, dirtyFields }) => {
        if (!isDirty) {
          debouncedUpdateUser.cancel()
          return
        }

        const updatePayload: {
          id: string
          roles_ids?: string[]
          employee_ext_id?: string
        } = {
          id: member.external_id || member.member_id,
        }

        let hasUpdates = false

        if (dirtyFields?.role_id) {
          const newRoleId = values.role_id
          updatePayload.roles_ids =
            newRoleId === DEFAULT_USER_ROLE.id ? [] : [newRoleId]
          hasUpdates = true
        }

        if (dirtyFields?.employee_ext_id) {
          updatePayload.employee_ext_id = values.employee_ext_id
          hasUpdates = true
        }

        if (hasUpdates) {
          debouncedUpdateUser(updatePayload)
        }
      },
    })
    return () => callback()
  }, [debouncedUpdateUser, member.external_id, member.member_id, subscribe])

  useEffect(() => {
    reset(defaultValues)
  }, [reset, defaultValues])

  if (!loggedInMember) return null

  return (
    <Stack spacing={2}>
      <div>
        <LargeHeading
          heading={member.name}
          subHeading={`User${isLoggedInMember ? ' (You)' : ''}`}
          actions={
            <>
              {['pending', 'invited'].includes(member.status) && (
                <Button
                  disabled={isInvitePending}
                  onClick={() => {
                    inviteMember({ email_address: member.email_address })
                  }}
                  size="small"
                  variant="text"
                >
                  Resend Email
                </Button>
              )}
              <CopyIDButton
                stringToCopy={member.external_id || member.member_id}
              />
            </>
          }
        />
      </div>
      <Stack spacing={3}>
        <LabeledData label="Name" data={member.name} />
        <LabeledData label="Email" data={member.email_address} />
        {canUpdateRole ? (
          <PixyDocsForm
            methods={methods}
            onSubmit={() => {}}
            style={{ width: 300 }}
          >
            <Stack spacing={3}>
              <FormTextField
                name="employee_ext_id"
                fullWidth
                label="Employee ID"
              />
              <UserRolesPicker />
            </Stack>
          </PixyDocsForm>
        ) : (
          <>
            <LabeledData
              label="Employee ID"
              data={
                member.trusted_metadata.employee_ext_id as string | undefined
              }
            />
            <LabeledData
              label="Role"
              data={
                isSuperUserVisible
                  ? // @ts-expect-error -- TODO: remove primeAdminsMap at some point
                    primeAdminsMap[member.email_address] || memberRole.id
                  : memberRole.id
              }
            />
          </>
        )}
      </Stack>
    </Stack>
  )
}
